import {
  BlockReferenceContents,
  ExegesisSlateBlock,
  TagMentionNode,
} from '@rhizome/pgtypes';
import { BLOCK_REFERENCE_MENTION } from '@rhizome/shared-slate-utils';
import {
  MARK_BOLD,
  MARK_ITALIC,
  MARK_STRIKETHROUGH,
  MARK_SUBSCRIPT,
  MARK_SUPERSCRIPT,
  MARK_UNDERLINE,
} from '@udecode/plate-basic-marks';
import { withProps } from '@udecode/plate-core';
import { ELEMENT_H1, ELEMENT_H2, ELEMENT_H3 } from '@udecode/plate-heading';
import { ELEMENT_IMAGE } from '@udecode/plate-image';
import { ELEMENT_LINK, LinkNodeData } from '@udecode/plate-link';
import { ELEMENT_MENTION, ELEMENT_MENTION_INPUT } from '@udecode/plate-mention';
import { ELEMENT_PARAGRAPH } from '@udecode/plate-paragraph';
import {
  StyledElement,
  StyledElementProps,
  StyledLeaf,
} from '@udecode/plate-styled-components';
import { ImageElement } from '@udecode/plate-ui-image';
import { LinkElement } from '@udecode/plate-ui-link';
import {
  MentionElementProps,
  MentionInputElement,
  MentionInputElementProps,
} from '@udecode/plate-ui-mention';
import { FunctionComponent } from 'react';
import { css } from 'styled-components';
import { CreateBlockReferenceSlateComponent } from '../plugins/BlockReferenceComponent';
import { CreateMentionSlateComponent } from '../plugins/TagMentionSlateComponent';
import { createBlockWrapper } from './BlockWrapper';

type ComponentMap = {
  h1: FunctionComponent<any>;
  h2: FunctionComponent<any>;
  h3: FunctionComponent<any>;
  p: (props: {
    element: ExegesisSlateBlock<TagMentionNode>;
    attributes: any;
  }) => JSX.Element;
  img: (props: {
    element: ExegesisSlateBlock<TagMentionNode>;
    attributes: any;
  }) => JSX.Element;
  a: (
    props: StyledElementProps<LinkNodeData, Record<string, unknown>>
  ) => JSX.Element;
  mention: ({
    attributes,
    element,
    prefix,
    children,
  }: MentionElementProps) => JSX.Element;
  block_reference: ({
    attributes,
    element,
    prefix,
    children,
  }: MentionElementProps & {
    element: BlockReferenceContents & {
      children: any[];
    };
  }) => JSX.Element;
  mention_input: (props: MentionInputElementProps) => JSX.Element;
  bold: FunctionComponent<any>;
  italic: FunctionComponent<any>;
  strikethrough: FunctionComponent<any>;
  subscript: FunctionComponent<any>;
  superscript: FunctionComponent<any>;
  underline: FunctionComponent<any>;
};

const components: ComponentMap = {
  // block elements
  [ELEMENT_H1]: createBlockWrapper(
    withProps(StyledElement, {
      as: 'h1',
      styles: {
        root: css`
          margin: 2em 0 4px;
          font-size: 1.875rem;
          font-weight: 500;
          line-height: 1.5;
        `,
      },
    })
  ),
  [ELEMENT_H2]: createBlockWrapper(
    withProps(StyledElement, {
      as: 'h2',
      styles: {
        root: css`
          margin: 1.4em 0 1px;
          font-size: 1.5rem;
          font-weight: 500;
          line-height: 1.5;
        `,
      },
    })
  ),
  [ELEMENT_H3]: createBlockWrapper(
    withProps(StyledElement, {
      as: 'h3',
      styles: {
        root: css`
          margin: 1em 0 1px;
          font-size: 1.25rem;
          font-weight: 500;
          line-height: 1.5;
          color: #434343;
        `,
      },
    })
  ),
  [ELEMENT_PARAGRAPH]: createBlockWrapper(
    withProps(StyledElement, {
      as: 'div',
      styles: {
        root: css`
          margin: 0px;
          font-size: 1.15rem;
          line-height: 1.5;
        `,
      },
      prefixClassNames: 'p',
    })
  ),
  [ELEMENT_IMAGE]: createBlockWrapper(
    withProps(ImageElement, {
      styles: {
        root: {
          userSelect: 'none',
        },
      },
      align: 'left',
      caption: {
        disabled: true,
      },
    })
  ),

  // inline elements
  [ELEMENT_LINK]: LinkElement,
  [ELEMENT_MENTION]: CreateMentionSlateComponent(true),
  [BLOCK_REFERENCE_MENTION]: CreateBlockReferenceSlateComponent(true),
  [ELEMENT_MENTION_INPUT]: MentionInputElement,

  [MARK_BOLD]: withProps(StyledLeaf, { as: 'strong' }),
  [MARK_ITALIC]: withProps(StyledLeaf, { as: 'em' }),
  [MARK_STRIKETHROUGH]: withProps(StyledLeaf, { as: 's' }),
  [MARK_SUBSCRIPT]: withProps(StyledLeaf, { as: 'sub' }),
  [MARK_SUPERSCRIPT]: withProps(StyledLeaf, { as: 'sup' }),
  [MARK_UNDERLINE]: withProps(StyledLeaf, { as: 'u' }),
};

export const createPlateUI = (overrideByKey?: Partial<ComponentMap>) => {
  if (overrideByKey) {
    (Object.keys(overrideByKey) as (keyof ComponentMap)[]).forEach(
      (key: keyof ComponentMap) => {
        const newComp = overrideByKey[key];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore: - we know that the key is valid
        components[key] = newComp;
      }
    );
  }
  return components;
};
